import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import {
  AccountsandFinanceurl,
  userManagementUrl,
} from "../../../../config";
import Header from "../../../../components/Header";
import {
  AllDocumentsClass,
  AllDocumentsTypes,
  allUsersQuery,
} from "../../../../data/Axios/queries";

const base_url = AccountsandFinanceurl.uri;
const base_url_userManagement = userManagementUrl.uri;

const DocumentApproversForm = (props) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const [documentClasses, setDocumentClasses] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const initialValues = {
    id: props.data?.id || "",
    documentClassId: props.data?.documentClassId || "",
    documentTypeId: props.data?.documentTypeId || "",
    approverUserId: props.data?.approverUserId || "",
    isFinalApprover: props.data?.isFinalApprover || false,
    isActive: props.data?.isActive ?? true,
    createdBy: props.data?.createdBy || userId,
    companyId: props.data?.companyId || companyId,
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const docClassRes = await graphqlQuery(AllDocumentsClass, base_url);
        const docTypesRes = await graphqlQuery(AllDocumentsTypes, base_url);
        const usersRes = await graphqlQuery(
          allUsersQuery,
          base_url_userManagement
        );

        if (docClassRes) setDocumentClasses(docClassRes);
        if (docTypesRes) setDocumentTypes(docTypesRes);
        if (usersRes) setUsers(usersRes);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const validationSchema = yup.object().shape({
    documentClassId: yup.number().required("Document Class is required"),
    documentTypeId: yup.number().required("Document Type is required"),
    approverUserId: yup.number().required("Approver is required"),
    isFinalApprover: yup.boolean(),
    isActive: yup.boolean(),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { id, isFinalApprover, isActive, ...rest } = values;

      const mutationData = {
        ...(props.isEditing && { id }),
        ...rest,
        isFinalApprover: isFinalApprover ? 1 : 0,
        isActive: isActive ? 1 : 0,
        companyId,
        createdBy: userId,
      };

      const response = await generateAndExecuteMutation(
        props.isEditing
          ? "updateDocumentApprover"
          : "createDocumentApprovers",
        mutationData,
        props.isEditing
          ? "updatedDocumentApprover"
          : "newDocumentApprover",
        base_url
      );

      if (Object.values(response)[0]) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Document approver ${
            props.isEditing ? "updated" : "created"
          } successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to save document approver.",
      });
    }

    setSubmitting(false);
  };

  if (loading) return <CircularProgress />;

  return (
    <Box m="20px">
      <Header
        title={
          props.isEditing
            ? "Edit Document Approver"
            : "Create Document Approver"
        }
        subtitle="Configure document approval workflow"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => {
          const filteredDocumentTypes = documentTypes.filter(
            (type) =>
              type.documentClassId === Number(values.documentClassId)
          );

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                }}
              >
                {/* Document Class */}
                <TextField
                  select
                  fullWidth
                  variant="filled"
                  label="Document Class *"
                  name="documentClassId"
                  value={values.documentClassId}
                  onChange={(e) => {
                    handleChange(e);
                    setFieldValue("documentTypeId", "");
                  }}
                  onBlur={handleBlur}
                  error={
                    touched.documentClassId &&
                    !!errors.documentClassId
                  }
                  helperText={
                    touched.documentClassId &&
                    errors.documentClassId
                  }
                  sx={{ gridColumn: "span 2" }}
                >
                  {documentClasses.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.documentClassTypeName}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Document Type */}
                <TextField
                  select
                  fullWidth
                  variant="filled"
                  label="Document Type *"
                  name="documentTypeId"
                  value={values.documentTypeId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.documentTypeId &&
                    !!errors.documentTypeId
                  }
                  helperText={
                    touched.documentTypeId &&
                    errors.documentTypeId
                  }
                  sx={{ gridColumn: "span 2" }}
                  disabled={!values.documentClassId}
                >
                  {filteredDocumentTypes.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.documentTypeName}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Approver User */}
                <TextField
                  select
                  fullWidth
                  variant="filled"
                  label="Approver User *"
                  name="approverUserId"
                  value={values.approverUserId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.approverUserId &&
                    !!errors.approverUserId
                  }
                  helperText={
                    touched.approverUserId &&
                    errors.approverUserId
                  }
                  sx={{ gridColumn: "span 2" }}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Final Approver */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.isFinalApprover}
                      onChange={(e) =>
                        setFieldValue(
                          "isFinalApprover",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Final Approver"
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>

              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : null
                  }
                  sx={{
                    backgroundColor: "#00A676",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#00A676",
                      boxShadow:
                        "0px 4px 12px rgba(0, 166, 118, 0.3)",
                    },
                  }}
                >
                  {isSubmitting
                    ? props.isEditing
                      ? "Updating..."
                      : "Saving..."
                    : props.isEditing
                    ? "Update Approver"
                    : "Save Approver"}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default DocumentApproversForm;